import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { supabase } from '../../supabaseClient.js';
import { seedDefaultTemplates } from '../../utils/seedDefaultTemplates.js';
import { setLoginStatus, setUserDetails, setCurrentOrg, setAllOrgs, setRole } from '../../store.js';
import useAlert from '../../alert/useAlert.js';
import Step1Organization from './Step1Organization.js';
import Step2AboutYou from './Step2AboutYou.js';
import Step3ClinicLocation from './Step3ClinicLocation.js';
import Step4Verification from './Step4Verification.js';
import Step5PublicProfile from './Step5PublicProfile.js';
import Step6ClientFeatures from './Step6ClientFeatures.js';
import Step7PlanOverview from './Step7PlanOverview.js';

const TOTAL_STEPS = 7;

/**
 * 7-step onboarding wizard container.
 *
 * Progress lives in user_accounts.onboarding_step: 1-7 = step to resume at,
 * NULL = onboarding complete (all pre-wizard accounts were backfilled NULL).
 * Each step saves its own data, then the container persists the step pointer,
 * so closing the tab mid-flow resumes exactly where the user left off.
 */
const Onboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setAlert } = useAlert();

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [userId, setUserId] = useState(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [invitedMetadata, setInvitedMetadata] = useState(null);
  const [org, setOrg] = useState(null); // { id, name, role }

  const initials = (displayName || email || '')
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');

  const fetchOrg = useCallback(async (uid) => {
    const { data: memberships } = await supabase
      .from('team_users')
      .select('team_id, role')
      .eq('user_id', uid)
      .order('created_at')
      .limit(1);
    const membership = memberships && memberships[0];
    if (!membership?.team_id) return null;
    const { data: teams } = await supabase.from('teams').select('name').eq('id', membership.team_id);
    let teamName = '';
    if (teams && teams[0]) {
      const decrypted = await supabase.rpc('decrypt_text', { encrypted_data: teams[0].name });
      teamName = decrypted.data || '';
    }
    return { id: membership.team_id, name: teamName, role: membership.role };
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }
      const user = session.user;
      setUserId(user.id);
      setEmail(user.email || '');
      // Both legacy signup and direct invitation metadata are locators only;
      // org_accept_invitation verifies the actual server-side invitation.
      setInvitedMetadata(user.user_metadata?.userData ||
        (user.user_metadata?.team_id ? user.user_metadata : null));

      const { data: account, error } = await supabase
        .from('user_accounts')
        .select('onboarding_step, name')
        .eq('id', user.id)
        .maybeSingle();
      if (error || !account) {
        setAlert('Unable to load your account. Please log in again.', 'error');
        navigate('/login');
        return;
      }
      if (account.onboarding_step === null || account.onboarding_step === undefined) {
        // Onboarding already finished (or pre-wizard account) — not for us.
        navigate('/login');
        return;
      }
      // Idempotent — covers wizard entrants who never ran the emailVerify
      // page's seeding (login self-heal, backfilled stranded accounts).
      await seedDefaultTemplates(user.id);
      if (account.name) {
        const decrypted = await supabase.rpc('decrypt_text', { encrypted_data: account.name });
        setDisplayName(decrypted.data || '');
      }
      const existingOrg = await fetchOrg(user.id);
      if (existingOrg) setOrg(existingOrg);

      setStep(Math.min(Math.max(account.onboarding_step, 1), TOTAL_STEPS));
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the pointer, then move the UI. Failing to persist is not fatal
  // (the user just resumes one step earlier next time).
  const goToStep = async (nextStep) => {
    const clamped = Math.min(Math.max(nextStep, 1), TOTAL_STEPS);
    const { error } = await supabase
      .from('user_accounts')
      .update({ onboarding_step: clamped })
      .eq('id', userId);
    if (error) console.error('Failed to persist onboarding step:', error);
    setStep(clamped);
    window.scrollTo({ top: 0 });
  };

  // Mirror of the post-signup login bootstrap (CreateProfile.loginUser):
  // hydrate redux from the DB, mark onboarding done, enter the app.
  const finalize = async (destination = '/dashboard', navState = undefined) => {
    try {
      const { error } = await supabase
        .from('user_accounts')
        .update({ onboarding_step: null })
        .eq('id', userId);
      if (error) throw error;

      const { data: rows } = await supabase.from('user_accounts').select('*').eq('id', userId);
      const account = rows && rows[0];
      if (!account) throw new Error('Account not found');
      const [nameRes, emailRes, practiceRes] = await Promise.all([
        supabase.rpc('decrypt_text', { encrypted_data: account.name }),
        supabase.rpc('decrypt_text', { encrypted_data: account.email }),
        supabase.rpc('decrypt_text', { encrypted_data: account.practicetype }),
      ]);

      const { data: memberships } = await supabase
        .from('team_users')
        .select('team_id, role')
        .eq('user_id', userId);
      const teamIds = (memberships || []).map((m) => m.team_id).filter(Boolean);
      const orgs = [];
      for (const teamId of teamIds) {
        const { data: teams } = await supabase.from('teams').select('name').eq('id', teamId);
        if (teams && teams[0]) {
          const decrypted = await supabase.rpc('decrypt_text', { encrypted_data: teams[0].name });
          orgs.push({ id: teamId, name: decrypted.data || '' });
        }
      }

      if (orgs.length > 0) {
        dispatch(setCurrentOrg(orgs[0]));
        dispatch(setAllOrgs(orgs));
        const membership = (memberships || []).find((m) => m.team_id === orgs[0].id);
        dispatch(setRole(membership?.role || 'Member'));
      }
      dispatch(setUserDetails({
        id: userId,
        email: emailRes.data || email,
        name: nameRes.data || '',
        practicetype: practiceRes.data || '',
        mode: 'Mental Health',
        data_retention_days: account.data_retention_days,
      }));
      dispatch(setLoginStatus(true));
      navigate(destination, navState ? { state: navState } : undefined);
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      setAlert('Something went wrong finishing setup. Please try again.', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f6f8f9' }}>
        <CircularProgress sx={{ color: '#29C0BB' }} />
      </Box>
    );
  }

  const ctx = { userId, email, initials, invitedMetadata, org, setOrg, displayName, setDisplayName };
  const stepProps = {
    ctx,
    onContinue: () => goToStep(step + 1),
    onSkip: () => goToStep(step + 1),
    onBack: () => goToStep(step - 1),
    // Stepper clicks: only completed steps are clickable, so this only goes back.
    onStepClick: (target) => { if (target < step) goToStep(target); },
  };

  switch (step) {
    case 1: return <Step1Organization {...stepProps} />;
    case 2: return <Step2AboutYou {...stepProps} />;
    case 3: return <Step3ClinicLocation {...stepProps} />;
    case 4: return <Step4Verification {...stepProps} />;
    case 5: return <Step5PublicProfile {...stepProps} />;
    case 6: return <Step6ClientFeatures {...stepProps} />;
    case 7: return <Step7PlanOverview {...stepProps} onFinish={finalize} />;
    default: return null;
  }
};

export default Onboarding;
